import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    LayoutDashboard, Package, Factory, Hammer, Warehouse, MapPin,
    Layers, Truck, FileText, LogOut, Menu, X, Users, Shield, Ribbon, ShieldCheck, Cog, Wind,
} from "lucide-react";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

const TURNO_ORDER = { I: 1, II: 2, III: 3 };

// `section`: usato solo per il sottotitolo dinamico della sidebar.
const MENU = [
    { to: "/", label: "Cruscotto", icon: LayoutDashboard, tid: "nav-dashboard", perm: "dashboard.view", section: "programma" },
    { to: "/articoli", label: "Articoli", icon: Package, tid: "nav-articoli", permAny: ["articoli.view", "art_estr.view"], section: "programma" },
    { to: "/estrusione/ordini", label: "Ordini produzione estrusori", icon: Factory, tid: "nav-ord-estr", perm: "ord_estr.view", section: "estrusione" },
    { to: "/ordini-produzione", label: "Ordini produzione saldatrici", icon: Factory, tid: "nav-ordini", perm: "ordini.view", section: "saldatura" },
    { to: "/estrusione/produzione", label: "Produzione estrusori (Operatore)", icon: Wind, tid: "nav-prod-estr", perm: "prod_estr.view", section: "estrusione" },
    { to: "/estrusione/bobine", label: "Bobine prodotte", icon: Package, tid: "nav-bobine-mag", perm: "prod_estr.view", section: "estrusione" },
    { to: "/produzione", label: "Produzione saldatrici (Operatore)", icon: Hammer, tid: "nav-produzione", perm: "produzione.view", section: "saldatura" },
    { to: "/pedane", label: "Pedane", icon: Layers, tid: "nav-pedane", perm: "pedane.view", section: "programma", hidden: true },
    { to: "/trasferimento-magazzino", label: "Sposta Pedana", icon: Warehouse, tid: "nav-mag-int", perm: "sposta_pedana.view", section: "programma", hidden: true },
    { to: "/uscita-merce", label: "Uscita Merce", icon: Truck, tid: "nav-uscita", perm: "uscita_merce.view", section: "programma" },
    { to: "/documenti", label: "Distinte & Documenti", icon: FileText, tid: "nav-documenti", perm: "documenti.view", section: "programma" },
    { to: "/utenti", label: "Utenti", icon: Users, tid: "nav-utenti", adminOnly: true, section: "programma" },
    { to: "/impostazioni", label: "Impostazioni", icon: Cog, tid: "nav-impostazioni", permAny: ["impostazioni.stampanti","anagrafiche.view","ubicazioni.view","macchine_estr.edit"], section: "programma" },
];

const SECTION_SUBTITLE = {
    saldatura: "Linea Saldatura",
    estrusione: "Linea Estrusione",
    programma: "Programma produzione",
};

function currentMenuItem(pathname) {
    // match esatto per "/", altrimenti prefisso più lungo
    if (pathname === "/") return MENU[0];
    // Alias route: /anagrafiche, /ubicazioni, /permessi → Impostazioni
    if (pathname === "/anagrafiche" || pathname === "/ubicazioni" || pathname === "/permessi") {
        return MENU.find(m => m.to === "/impostazioni") || MENU[0];
    }
    const matches = MENU.filter(m => m.to !== "/" && pathname.startsWith(m.to));
    matches.sort((a, b) => b.to.length - a.to.length);
    return matches[0] || MENU[0];
}

const ROLE_LABEL = {
    admin: "Admin", ufficio: "Ufficio Produzione", operatore: "Operatore",
    operatore_estrusione: "Operatore Estrusione",
    magazziniere: "Magazziniere", segreteria: "Segreteria",
};

// Hook: conta gli ordini "Prima di me — non finiti" per il badge sidebar
function usePrimaDiMeCount(user) {
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (user?.role !== "operatore") { setCount(0); return; }
        const impianto = localStorage.getItem(`op-impianto-${user?.id}`) || "";
        const turno = localStorage.getItem(`op-turno-${user?.id}-${new Date().toISOString().slice(0,10)}`) || "";
        if (!impianto || !turno) { setCount(0); return; }
        const today = new Date().toISOString().slice(0,10);
        const curT = TURNO_ORDER[turno] || 0;
        let cancelled = false;
        const fetchCount = async () => {
            try {
                const { data } = await api.get(`/ordini?impianto_id=${impianto}&stato=aperto`);
                const n = data.filter(o => (o.cartoni_prodotti || 0) < o.cartoni_pianificati && (
                    (o.data_lavorazione && o.data_lavorazione < today) ||
                    (o.data_lavorazione === today && curT > 0 && TURNO_ORDER[o.turno] > 0 && TURNO_ORDER[o.turno] < curT)
                )).length;
                if (!cancelled) setCount(n);
            } catch { if (!cancelled) setCount(0); }
        };
        fetchCount();
        const iv = setInterval(fetchCount, 30000);
        const onStorage = () => fetchCount();
        window.addEventListener("storage", onStorage);
        return () => { cancelled = true; clearInterval(iv); window.removeEventListener("storage", onStorage); };
    }, [user?.id, user?.role]);
    return count;
}

function BadgePill({ n }) {
    if (!n) return null;
    return (
        <span data-testid="badge-prima-di-me"
              className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold">
            {n > 99 ? "99+" : n}
        </span>
    );
}

export default function AppShell({ children }) {
    const { user, logout, can } = useAuth();
    const nav_ = useNavigate();
    const loc = useLocation();
    const [open, setOpen] = useState(false);
    const isAdmin = user?.role === "admin";
    const items = MENU.filter(m => {
        if (m.hidden) return false;
        if (m.adminOnly) return isAdmin;
        if (m.permAny) return isAdmin || m.permAny.some(p => can(p));
        return can(m.perm);
    });
    const primaCount = usePrimaDiMeCount(user);

    const activeItem = currentMenuItem(loc.pathname);
    const subtitle = SECTION_SUBTITLE[activeItem?.section] || "Programma produzione";

    useEffect(() => {
        document.title = `PRODUX · ${subtitle} — ${activeItem?.label || ""}`.trim();
    }, [subtitle, activeItem?.label]);

    const doLogout = async () => { await logout(); nav_("/login"); };

    return (
        <div className="min-h-screen flex text-slate-100 relative z-10">
            <aside className="hidden lg:flex flex-col w-64 border-r border-slate-800/80 bg-slate-950/60 backdrop-blur-xl sticky top-0 h-screen">
                <div className="p-5 border-b border-slate-800/80">
                    <div className="font-display text-xl font-extrabold text-amber-400 leading-none">PRODUX</div>
                    <div className="mt-1 text-[10px] tracking-[0.25em] text-slate-500 font-mono uppercase" data-testid="sidebar-subtitle">{subtitle}</div>
                </div>
                <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                    {items.map(({ to, label, icon: Ic, tid }) => (
                        <NavLink key={to} to={to} end={to === "/"} data-testid={tid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                                    isActive
                                        ? "bg-amber-500/10 text-amber-300 border border-amber-500/30"
                                        : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/50 border border-transparent"
                                }`}>
                            <Ic size={16} strokeWidth={2} />
                            <span>{label}</span>
                            {to === "/produzione" && <BadgePill n={primaCount} />}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-4 border-t border-slate-800/80 space-y-2">
                    <div className="flex items-center gap-2 text-xs">
                        <Shield size={12} className="text-amber-400" />
                        <span className="text-amber-300 font-mono uppercase tracking-wider">{ROLE_LABEL[user?.role] || user?.role}</span>
                    </div>
                    <div className="text-[11px] text-slate-400 truncate" data-testid="user-email">{user?.email || (user?.codice ? `codice ${user.codice}` : "-")}</div>
                    <Button variant="outline" onClick={doLogout} data-testid="button-logout"
                        className="w-full bg-transparent border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white gap-2">
                        <LogOut size={14} /> Esci
                    </Button>
                </div>
            </aside>

            <div className="lg:hidden fixed top-0 left-0 right-0 z-40 border-b border-slate-800/80 bg-slate-950/90 backdrop-blur-xl">
                <div className="flex items-center justify-between px-4 py-3">
                    <div>
                        <div className="font-display text-lg font-extrabold text-amber-400 leading-none">PRODUX</div>
                        <div className="text-[9px] tracking-[0.25em] text-slate-500 font-mono uppercase" data-testid="sidebar-subtitle-mobile">{subtitle}</div>
                    </div>
                    <button data-testid="button-mobile-menu" onClick={() => setOpen(!open)} className="p-2 rounded-lg bg-slate-800/60 text-slate-100">
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
                {open && (
                    <div className="border-t border-slate-800/80 p-3 space-y-1">
                        {items.map(({ to, label, icon: Ic, tid }) => (
                            <NavLink key={to} to={to} end={to === "/"} onClick={() => setOpen(false)} data-testid={`m-${tid}`}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                                        isActive ? "bg-amber-500/15 text-amber-300" : "text-slate-300 hover:bg-slate-800/60"
                                    }`}>
                                <Ic size={16} /><span>{label}</span>
                                {to === "/produzione" && <BadgePill n={primaCount} />}
                            </NavLink>
                        ))}
                        <Button onClick={doLogout} data-testid="button-logout-mobile" variant="outline"
                                className="w-full mt-2 bg-transparent border-slate-700 text-slate-300 gap-2">
                            <LogOut size={14} /> Esci
                        </Button>
                    </div>
                )}
            </div>

            <main className="flex-1 min-w-0 px-4 sm:px-8 py-6 lg:py-10 pt-20 lg:pt-10">
                <div className="max-w-7xl mx-auto fade-up">{children}</div>
            </main>
        </div>
    );
}
